<template>
  <section class="recommend-video" v-if="videos.length > 0">
    <div class="section-header">
      <div class="title-wrapper">
        <h2 class="section-title">ວີດີໂອແນະນຳ</h2>
        <div class="title-underline"></div>
      </div>
    </div>

    <div v-if="loading" class="state-box">
      <div class="spinner" />
    </div>

    <div v-else-if="error" class="state-box error">
      <p>ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ</p>
    </div>

    <div v-else-if="videos.length" class="carousel-wrapper">
      <!-- Prev Side Card -->
      <div class="side-card side-card--left" @click="prev">
        <img :src="prevVideo.thumbnail_url" :alt="prevVideo.head_title" />
        <div class="side-card__overlay">
          <span class="arrow-btn">‹</span>
        </div>
      </div>

      <!-- Main Player -->
      <div
        class="main-player"
        @touchstart="onTouchStart"
        @touchend="onTouchEnd"
      >
        <transition name="fade" mode="out-in">
          <div :key="currentIndex" class="video-container">
            <iframe
              v-if="embedUrl"
              :src="embedUrl"
              class="video-iframe"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            />
            <div v-else class="video-fallback">
              <img
                :src="currentVideo.thumbnail_url"
                :alt="currentVideo.head_title"
              />
              <a :href="currentVideo.url_video" target="_blank" class="play-btn"
                >▶</a
              >
            </div>
          </div>
        </transition>

        <!-- Caption -->
        <div class="caption">
          <h3 class="caption__title">{{ currentVideo.head_title }}</h3>
          <p class="caption__desc">{{ currentVideo.description }}</p>
        </div>

        <!-- Dots -->
        <div class="dots">
          <button
            v-for="(_, i) in videos"
            :key="i"
            class="dot"
            :class="{ 'dot--active': i === currentIndex }"
            @click="goTo(i)"
          />
        </div>
      </div>

      <!-- Next Side Card -->
      <div class="side-card side-card--right" @click="next">
        <img :src="nextVideo.thumbnail_url" :alt="nextVideo.head_title" />
        <div class="side-card__overlay">
          <span class="arrow-btn">›</span>
        </div>
      </div>
    </div>

    <div v-else class="state-box">
      <p>ບໍ່ມີວິດີໂອທີ່ສາມາດເບິ່ງໄດ້ໃນເວລານີ້</p>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useQuery } from "@vue/apollo-composable";
import gql from "graphql-tag";

// ===== GRAPHQL QUERY =====
const GET_RECOM_VDO = gql`
  query GetRecomVDO {
    recommendVideo_recomVDO(order_by: { order_number: asc }) {
      id
      url_video
      thumbnail_url
      head_title
      description
      order_number
      status
    }
  }
`;

// ===== APOLLO =====
const { result, loading, error } = useQuery(GET_RECOM_VDO);

// เรียงตาม order_number (Hasura จัดการให้แล้ว) และกรองเฉพาะ status = true
const videos = computed(() =>
  (result.value?.recommendVideo_recomVDO ?? []).filter((v) => v.status === true)
);

// ===== STATE =====
const currentIndex = ref(0);

// ===== COMPUTED =====
const currentVideo = computed(() => videos.value[currentIndex.value] ?? {});

const prevIndex = computed(
  () => (currentIndex.value - 1 + videos.value.length) % videos.value.length
);
const nextIndex = computed(
  () => (currentIndex.value + 1) % videos.value.length
);

const prevVideo = computed(() => videos.value[prevIndex.value] ?? {});
const nextVideo = computed(() => videos.value[nextIndex.value] ?? {});

/** แปลง YouTube URL หลายรูปแบบ → embed URL */
const embedUrl = computed(() => {
  const url = currentVideo.value?.url_video;
  if (!url) return null;

  let videoId = null;

  // youtu.be/ID
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) videoId = shortMatch[1];

  // youtube.com/watch?v=ID
  const watchMatch = url.match(/[?&]v=([^?&]+)/);
  if (watchMatch) videoId = watchMatch[1];

  // youtube.com/embed/ID
  const embedMatch = url.match(/embed\/([^?&]+)/);
  if (embedMatch) videoId = embedMatch[1];

  return videoId
    ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`
    : null;
});

// ===== NAVIGATION =====
function prev() {
  currentIndex.value = prevIndex.value;
}
function next() {
  currentIndex.value = nextIndex.value;
}
function goTo(i) {
  currentIndex.value = i;
}

// ===== KEYBOARD =====
function onKey(e) {
  if (e.key === "ArrowLeft") prev();
  if (e.key === "ArrowRight") next();
}

onMounted(() => window.addEventListener("keydown", onKey));
onUnmounted(() => window.removeEventListener("keydown", onKey));

// เพิ่มตัวแปร
const touchStartX = ref(0);
const touchEndX = ref(0);

// เพิ่ม functions
function onTouchStart(e) {
  touchStartX.value = e.touches[0].clientX;
}

function onTouchEnd(e) {
  touchEndX.value = e.changedTouches[0].clientX;
  const diff = touchStartX.value - touchEndX.value;

  if (Math.abs(diff) > 50) {
    // swipe ต้องเกิน 50px
    if (diff > 0) next(); // swipe ซ้าย → next
    else prev(); // swipe ขวา → prev
  }
}
</script>

<style scoped>
/* ========== CSS Variables ========== */
.recommend-video {
  --color-title: #344873;
  --color-red: #ea2127;
  --color-desc: #343d47;
  --color-bg: #f5f6f8;
  --color-dot: #c8cdd5;
  --radius-card: 18px;
  --radius-video: 22px;
  --shadow-card: 0 6px 28px rgba(52, 72, 115, 0.13);
  --shadow-video: 0 16px 48px rgba(52, 72, 115, 0.18);
  --transition: 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  width: 100%;
  padding: 56px 0 72px;
  background: #ffffff;
  font-family: "Sarabun", sans-serif;
}

/* ========== Section Title ========== */
.section-header {
  text-align: center;
  margin-bottom: 3.5rem;
}
.section-title {
  font-size: clamp(1.6rem, 3.5vw, 2.2rem);
  font-weight: 800;
  color: #344873;
  margin: 0 0 0.75rem;
  letter-spacing: -0.01em;
}

.title-wrapper {
  display: inline-block;
}
.title-underline {
  width: 100%;
  height: 0.25rem;
  background: #ea2127;
  border-radius: 99px;
  margin: 0 auto;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-16px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes expandLine {
  from {
    width: 0;
    opacity: 0;
  }
  to {
    width: 48px;
    opacity: 1;
  }
}

/* ========== Carousel Wrapper ========== */
.carousel-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 20px;
  animation: fadeUp 0.5s 0.15s ease both;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ========== Side Cards ========== */
.side-card {
  position: relative;
  width: 190px;
  min-width: 140px;
  height: 310px;
  border-radius: var(--radius-card);
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform var(--transition), box-shadow var(--transition);
  box-shadow: var(--shadow-card);
}

.side-card:hover {
  transform: scale(1.05) translateY(-4px);
  box-shadow: 0 16px 40px rgba(52, 72, 115, 0.22);
}

.side-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.55) saturate(0.8);
  transition: filter var(--transition), transform var(--transition);
}

.side-card:hover img {
  filter: brightness(0.75) saturate(1);
  transform: scale(1.06);
}

.side-card__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    to bottom,
    transparent 40%,
    rgba(52, 72, 115, 0.45)
  );
}

.arrow-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(6px);
  border: 1.5px solid rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: #fff;
  line-height: 1;
  user-select: none;
  transition: background var(--transition), transform var(--transition);
}

.side-card:hover .arrow-btn {
  background: rgba(234, 33, 39, 0.75);
  border-color: transparent;
  transform: scale(1.12);
}

/* ========== Main Player ========== */
.main-player {
  flex: 1;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 28px;
}

.video-container {
  width: 100%;
  border-radius: var(--radius-video);
  overflow: hidden;
  box-shadow: var(--shadow-video);
  background: #000;
  aspect-ratio: 16/9;
  position: relative;
}

.video-iframe {
  width: 100%;
  height: 100%;
  display: block;
}

/* Fallback */
.video-fallback {
  width: 100%;
  height: 100%;
  position: relative;
}

.video-fallback img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-btn {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: #fff;
  background: rgba(0, 0, 0, 0.35);
  text-decoration: none;
  transition: background 0.2s;
}

.play-btn:hover {
  background: rgba(0, 0, 0, 0.52);
}

/* ========== Caption ========== */
.caption {
  width: 100%;
  padding: 22px 4px 0;
  text-align: left;
}

.caption__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-red);
  margin: 0 0 8px;
  line-height: 1.5;
  transition: color 0.3s;
}

.caption__desc {
  font-size: 0.93rem;
  color: var(--color-desc);
  line-height: 1.7;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ========== Dots ========== */
.dots {
  display: flex;
  gap: 8px;
  margin-top: 20px;
  align-items: center;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: var(--color-dot);
  cursor: pointer;
  padding: 0;
  transition: background var(--transition), transform var(--transition),
    width var(--transition);
}

.dot--active {
  background: var(--color-red);
  width: 24px;
  border-radius: 4px;
  transform: none;
}

/* ========== Slide Fade transition ========== */
.fade-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ========== States ========== */
.state-box {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  color: #9aa3af;
  font-size: 1rem;
}

.state-box.error {
  color: var(--color-red);
}

.spinner {
  width: 44px;
  height: 44px;
  border: 4px solid #dde1e8;
  border-top-color: var(--color-title);
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ========== Responsive — Tablet ========== */
@media (max-width: 960px) {
  .side-card {
    width: 130px;
    min-width: 100px;
    height: 240px;
  }

  .main-player {
    padding: 0 14px;
  }
  .section-title {
    font-size: 1.7rem;
  }
}

/* ========== Responsive — Mobile ========== */
@media (max-width: 640px) {
  .recommend-video {
    padding: 36px 0 52px;
  }

  .section-title {
    font-size: 1.45rem;
    margin-bottom: 28px;
  }

  /* ซ่อน side cards แต่เพิ่ม swipe-hint arrows แทน */
  .side-card {
    display: none;
  }

  .carousel-wrapper {
    padding: 0;
    flex-direction: column;
    gap: 0;
  }

  .main-player {
    padding: 0 16px;
    width: 100%;
  }

  .video-container {
    border-radius: 14px;
  }

  .caption {
    padding: 16px 4px 0;
  }
  .caption__title {
    font-size: 1rem;
  }
  .caption__desc {
    font-size: 0.88rem;
    -webkit-line-clamp: 2;
  }

  /* Mobile nav row */
  .dots {
    margin-top: 16px;
    justify-content: center;
  }
}

/* ========== Responsive — Small Mobile ========== */
@media (max-width: 380px) {
  .section-title {
    font-size: 1.25rem;
  }
  .caption__title {
    font-size: 0.95rem;
  }
}
</style>